import {
  Accordion as UiAccordion,
  AccordionItem as UiAccordionItem
} from '@Pages/pageRuntime/components/ui/Accordion';
import { AccordionContent } from '@Pages/pageRuntime/components/ui/AccordionContent';
import { AccordionTrigger } from '@Pages/pageRuntime/components/ui/AccordionTrigger';
import type React from 'react';

type AccordionItem = {
  /**
   * Unique identifier for the item, used for open/close state.
   */
  value: string;

  /**
   * Trigger label shown for the item.
   */
  title: React.ReactNode;

  /**
   * Content revealed when the item is open.
   */
  content: React.ReactNode;
};

type AccordionProps = {
  /**
   * Accordion item definitions.
   */
  items: AccordionItem[];

  /**
   * Whether one or many items can be open at the same time.
   */
  type?: 'single' | 'multiple';

  /**
   * Initially opened item value (or values for `multiple`).
   */
  defaultValue?: string | string[];

  /**
   * Controlled open value(s).
   */
  value?: string | string[];

  /**
   * Called when the open value(s) change.
   */
  onValueChange?: (value: string | string[]) => void;

  /**
   * Whether all items can be closed when `type` is `single`.
   */
  collapsible?: boolean;

  className?: string;
};

/**
 * An expandable section list for FAQs, grouped details, and progressive disclosure.
 * Use concise titles and place longer content in each item's content node.
 *
 * @example
 * <Sdk.Components.Accordion items={[{ value: 'overview', title: 'Overview', content: <p>Summary</p> }]} />
 */
export function Accordion({
  items,
  type = 'single',
  defaultValue,
  value,
  onValueChange,
  collapsible = true,
  className
}: AccordionProps) {
  if (type === 'multiple') {
    return (
      <UiAccordion
        type="multiple"
        defaultValue={Array.isArray(defaultValue) ? defaultValue : undefined}
        value={Array.isArray(value) ? value : undefined}
        onValueChange={onValueChange as (value: string[]) => void}
        className={className}
      >
        {items.map((item) => (
          <UiAccordionItem key={item.value} value={item.value}>
            <AccordionTrigger>{item.title}</AccordionTrigger>
            <AccordionContent>{item.content}</AccordionContent>
          </UiAccordionItem>
        ))}
      </UiAccordion>
    );
  }

  return (
    <UiAccordion
      type="single"
      collapsible={collapsible}
      defaultValue={typeof defaultValue === 'string' ? defaultValue : undefined}
      value={typeof value === 'string' ? value : undefined}
      onValueChange={onValueChange as (value: string) => void}
      className={className}
    >
      {items.map((item) => (
        <UiAccordionItem key={item.value} value={item.value}>
          <AccordionTrigger>{item.title}</AccordionTrigger>
          <AccordionContent>{item.content}</AccordionContent>
        </UiAccordionItem>
      ))}
    </UiAccordion>
  );
}
